import { isRateLimited, RATE_LIMITED_MESSAGE } from "./rateLimitRetry";

/**
 * The server's refusal as a string, safe to hand to a notification.
 *
 * `AllExceptionsFilter` (equility-api `src/common/exception/exceptions.filter.ts`)
 * answers an HttpException with `{ statusCode, path, message: getResponse() }`,
 * and `getResponse()` is itself `{ message, error, statusCode }`. So the prose
 * is at `data.message.message`, and `data.message` is an OBJECT. Passing that
 * to antd's `notification.error({ description })` crashes the whole tree with
 * "Objects are not valid as a React child". An unhandled error escapes as a
 * flat string instead, so both shapes are read.
 */
function firstText(value: unknown): string | undefined {
	if (typeof value === "string" && value.trim()) return value;
	if (Array.isArray(value)) {
		const parts = value.filter(
			(v): v is string => typeof v === "string" && v.trim().length > 0,
		);
		if (parts.length > 0) return parts.join(", ");
	}
	return undefined;
}

export function getApiErrorMessage(error: unknown): string {
	// The throttler's own words are not for a reader (/qa r16 F-4).
	if (isRateLimited(error)) return RATE_LIMITED_MESSAGE;
	const message = (error as { response?: { data?: { message?: unknown } } })
		?.response?.data?.message;
	const nested =
		message && typeof message === "object" && !Array.isArray(message)
			? (message as { message?: unknown }).message
			: undefined;
	return (
		firstText(nested) ??
		firstText(message) ??
		(error instanceof Error ? error.message : undefined) ??
		"Something went wrong"
	);
}
